import { CurrentScene } from "@/modes/mode-selectors";
import { CurrentAreaData } from "@/modes/select-current-area-data";
import {
  computeDataSetCapturePointCloud,
  selectBestImage,
  selectComputeGaussianSplats,
  selectComputePointcloud,
  selectIsPanoWithDepth,
} from "@/modes/walk-mode/walk-scene-selectors";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { selectModeName } from "@/store/mode-selectors";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectCanReadPointCloud } from "@/store/subscription-features/subscription-features-selectors";
import { SubscriptionFeaturesState } from "@/store/subscription-features/subscription-features-slice";
import {
  isIElementOrthomosaic,
  isIElementPhotogrammetryScan,
} from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import {
  IElementsState,
  selectChildDepthFirst,
} from "@faro-lotv/app-component-toolbox";
import { isIElementImg2d } from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useMemo } from "react";
import { Vector3 } from "three";
import { FlyWalkSceneActiveElement } from "../flywalk-types";
import { selectIsPanoPairedWithPickingPointCloud } from "./use-dispatch-annotations";

/**
 * This is the most important hook in the flywalk mode data dispatch, since at any time
 * it computes the elements that are active in the mode.
 *
 * @param scene current scene data
 * @param area current area data
 * @param cameraPosition position of current camera
 * @param sceneFilter current scene filter
 * @returns List of currently active elements in flywalk mode
 */
export function useFlyWalkSceneElements(
  scene: CurrentScene,
  area: CurrentAreaData,
  cameraPosition: Vector3,
  sceneFilter: SceneFilter,
): Array<FlyWalkSceneActiveElement | undefined> {
  // The active elements in flywalk mode when the scene filter is not 'pano'
  const resultNotPano = useAppSelector(
    selectFlyWalkSceneElements(scene, area, sceneFilter),
    isEqual,
  );

  const { getState } = useAppStore();

  // The pano that may be the mode's main element is computed inside a useMemo,
  // and not inside a selector. This is because if the selector is memoized, it will
  // return memoized values for a given camera, scene, and scene filter.
  // Instead, the pano element may be different for the same camera, scene, and scene filter,
  // because the camera position may have changed therefore resulting in a change in the pano
  // that is closest to that position.
  const resultPano = useMemo(() => {
    if (sceneFilter !== SceneFilter.Pano) {
      return [];
    }
    const state = getState();
    if (isIElementImg2d(scene.activeElement)) {
      return [scene.activeElement];
    }
    if (
      isIElementPhotogrammetryScan(scene.activeElement) ||
      isIElementOrthomosaic(scene.activeElement)
    ) {
      const orthoMosaic = selectChildDepthFirst(
        scene.activeElement,
        isIElementOrthomosaic,
      )(state);
      return [orthoMosaic];
    }
    const pano = selectBestImage(scene, cameraPosition)(state);
    const result = [pano];

    const isPanoWithDepth = selectIsPanoWithDepth(pano)(state);
    const isPairedWithPickingPointCloud =
      selectIsPanoPairedWithPickingPointCloud(pano)(state);
    const isSplitMode = selectModeName(state) === "split";
    // If the main element is a pano paired with a picking point cloud and without depth,
    // and if we are not in splitscreen mode, then a point cloud is also loaded as the
    // overlay element, to enable measurement and annotation on the pano by picking the
    // point cloud.
    if (
      pano &&
      isPairedWithPickingPointCloud &&
      !isPanoWithDepth &&
      !isSplitMode
    ) {
      const pointCloud = selectComputePointcloud(scene, area)(state);
      if (pointCloud) {
        result.push(pointCloud);
      }
    }
    return result;
  }, [sceneFilter, scene, area, cameraPosition, getState]);

  return sceneFilter === SceneFilter.Pano ? resultPano : resultNotPano;
}

/**
 * Given the current scene and area data, compute the new active element desired
 * based on the flyWalk mode active type
 *
 * @param state The state of the app store
 * @param scene The current active scene
 * @param area The data available in the current area
 * @param sceneFilter The new desired scene filter
 * @returns The best IElement for the current flyWalk mode active type
 */
const selectFlyWalkSceneElements = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectCanReadPointCloud,
      (state: FlyWalkSceneElementsStore, scene: CurrentScene) => scene,
      (
        state: FlyWalkSceneElementsStore,
        scene: CurrentScene,
        area: CurrentAreaData,
      ) => area,
      (
        state: FlyWalkSceneElementsStore,
        scene: CurrentScene,
        area: CurrentAreaData,
        sceneFilter: SceneFilter,
      ) => sceneFilter,
    ],
    computeFlyWalkSceneElements,
  ),
);

type FlyWalkSceneElementsStore = OnlyIElementsStore & {
  subscriptionFeatures: SubscriptionFeaturesState;
};

function computeFlyWalkSceneElements(
  iElements: IElementsState,
  canReadPointCloud: boolean,
  scene: CurrentScene,
  area: CurrentAreaData,
  sceneFilter: SceneFilter,
): Array<FlyWalkSceneActiveElement | undefined> {
  const state = { iElements };
  switch (sceneFilter) {
    case SceneFilter.PointCloud: {
      return [selectComputePointcloud(scene, area)(state)];
    }
    case SceneFilter.GaussianSplats: {
      const splats = selectComputeGaussianSplats(scene, area)(state);
      // Only splats stored in a DataSetCapture have a sibling point cloud there. That point
      // cloud is loaded as the overlay so the zoom lens has a geometry to pick on, the same
      // way a depth-less odometry path pano is supported by a point cloud behind it.
      const pointCloud = canReadPointCloud
        ? computeDataSetCapturePointCloud(iElements, splats)
        : undefined;
      return [splats, pointCloud];
    }
    case SceneFilter.Cad: {
      return [scene.cad];
    }
    case SceneFilter.Overlay: {
      const pointCloud = selectComputePointcloud(scene, area)(state);
      const { cad } = scene;
      return [pointCloud, cad];
    }
    default:
      return [];
  }
}
